import React from 'react';
import { BSModal } from '~/components/Bootstrap';
import { Modal, Spinner } from 'react-bootstrap';
import { ModalTabs } from '~/components/Patient';
import { patientServiceEvents, ServicesContext, usePatientService } from '~/services';
import { ReminderAuditNotes, ReminderNotes } from '~/components/Patient/Notes/Reminders';
import { ReminderInfo } from './ReminderInfo';
import { TimelyActionInfo } from './TimelyActionInfo';

interface ReminderModalProps {
    reminder: ReminderType;
    patient: PatientType;
    className?: string;
}

export function ReminderModal({ reminder, patient, className = '' }: ReminderModalProps) {
    const servicesContext = React.useContext(ServicesContext);
    const { getGap } = usePatientService();
    const [show, setShow] = React.useState(false);
    const [tab, setTab] = React.useState('Reminder');
    const [loading, setLoading] = React.useState(false);
    const [formDirty, setFormDirty] = React.useState(false);
    const [statusDirty, setStatusDirty] = React.useState(false);

    const tabs = ['Reminder', 'Notes', 'History'];

    if (reminder?.timely_action) tabs.splice(1, 0, 'Info');

    async function performEdit(description: string, dueDate: string) {
        setLoading(true);
        await servicesContext.patient.editReminder(reminder, { description, due_at: dueDate });
        setLoading(false);
        setShow(false);
    }

    function close() {
        if (statusDirty) {
            // If the status has been updated inside the modal, after closing the modal
            // these will trigger refreshing the list for both the whole feed and
            // for the specific gap, if applicable.
            servicesContext.events.publish(patientServiceEvents.reminders_updated);
            const gapId = reminder.risk_gap?.id ?? reminder.foc_gap?.id ?? reminder.quality_gap?.id;
            if (gapId !== undefined) {
                getGap(gapId, reminder.type);
            }
        }
        setShow(false);
    }

    return (
        <>
            <button
                className={`link-text text-secondary edit-reminder-button ${className}`}
                type="button"
                onClick={() => setShow(true)}
            >
                <i className="fa-solid fa-pencil" title="View Reminder">
                    <span className="sr-only">View Reminder</span>
                </i>
            </button>
            <BSModal
                onHide={close}
                show={show}
                className="attestation-modal-card gap-modal"
                aria-labelledby="reminder-details-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="reminder-details-title" role="heading" aria-level={1}>
                        Reminder Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ModalTabs setTab={setTab} tab={tab} tabs={tabs} />
                    <div className={tab === 'Reminder' ? '' : 'd-none'}>
                        <ReminderInfo
                            reminder={reminder}
                            patient={patient}
                            performEdit={performEdit}
                            setFormDirty={setFormDirty}
                            setStatusDirty={setStatusDirty}
                        />
                    </div>
                    {tab === 'Info' && <TimelyActionInfo reminder={reminder} />}
                    {tab === 'Notes' && <ReminderNotes reminderId={reminder.id} />}
                    {tab === 'History' && (
                        <ReminderAuditNotes
                            notes={reminder.notes}
                            patientId={reminder.patient.id}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" type="button" onClick={close}>
                        Close
                    </button>
                    <button
                        className="btn btn-primary"
                        type="submit"
                        form="add-edit-reminder-form"
                        disabled={!formDirty || loading}
                    >
                        {loading && (
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                                role="status"
                                className="me-1"
                            />
                        )}
                        Save
                    </button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}
